
import { useEffect, useState, useRef, useCallback } from "react";
import { PixelDialogContent } from "./pixel/PixelDialogContent";
import { PixelStateCallbacks } from "@/hooks/pixel/usePixelState";
import { usePixelCreation } from "@/hooks/pixel/usePixelCreation";

interface AddPixelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitStateChange?: (isSubmitting: boolean) => void;
  onInstallationPhaseChange?: (isInstallation: boolean) => void;
}

export function AddPixelDialog({ 
  open, 
  onOpenChange, 
  onSubmitStateChange,
  onInstallationPhaseChange
}: AddPixelDialogProps) {
  const [internalOpen, setInternalOpen] = useState(open);
  const openedRef = useRef(false);
  const lastOpenChangeTimestampRef = useRef(Date.now());
  const prevOpenRef = useRef(open);
  const closeInProgressRef = useRef(false);
  const dialogClosedRef = useRef(false);
  
  useEffect(() => {
    // Ensure dialog is properly initialized as open on mount if open=true
    if (open && !openedRef.current) {
      openedRef.current = true;
      setInternalOpen(true);
    }
  }, [open]);
  
  const callbacks: PixelStateCallbacks = {
    onSubmitStateChange,
    onInstallationPhaseChange
  };

  const {
    pixelId,
    isSubmitting,
    installationPhase,
    createPixel,
    testPixel,
    allowClose,
    resetPixelId,
    completeCleanup
  } = usePixelCreation(callbacks);

  // NEW: When dialog closes completely, do full cleanup to allow reopening
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      console.log("Dialog closed completely, marking for cleanup");
      dialogClosedRef.current = true;
      
      // Ensure we do a complete cleanup to reset all state
      completeCleanup();
    }
    
    // When dialog opens after being closed, reset the closed flag
    if (!prevOpenRef.current && open) {
      console.log("Dialog reopened, resetting closed flag");
      dialogClosedRef.current = false;
    }
    
    prevOpenRef.current = open;
  }, [open, completeCleanup]);

  useEffect(() => {
    if (prevOpenRef.current !== open) {
      console.log(`AddPixelDialog open prop changed: ${prevOpenRef.current} -> ${open}`);
      
      if (closeInProgressRef.current && open) {
        console.log("Ignoring reopen during close operation");
        return;
      }
      
      const now = Date.now();
      lastOpenChangeTimestampRef.current = now;
      
      // Always update internal state when parent state changes
      setInternalOpen(open);
      prevOpenRef.current = open;
      
      // When dialog is explicitly closed from outside while in an active state
      if (!open && prevOpenRef.current && pixelId) {
        console.log("AddPixelDialog: Dialog closed from outside, resetting pixel state");
        closeInProgressRef.current = true;
        resetPixelId();
        
        setTimeout(() => {
          closeInProgressRef.current = false;
        }, 500);
      }
    }
  }, [open, pixelId, resetPixelId]);

  useEffect(() => {
    console.log("AddPixelDialog props/state:", { 
      open, 
      internalOpen,
      pixelId, 
      isSubmitting, 
      installationPhase,
      closeInProgress: closeInProgressRef.current,
      dialogClosed: dialogClosedRef.current
    });
    
    // Handle dialog reopening when in critical state
    if (!internalOpen && (isSubmitting || installationPhase)) {
      console.log("Critical state detected but dialog is closed, reopening");
      setInternalOpen(true);
      
      // Also notify parent
      if (!open) {
        onOpenChange(true);
      }
    }
  }, [open, internalOpen, pixelId, isSubmitting, installationPhase, onOpenChange]);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    const shouldBlockClose = isSubmitting;
    const now = Date.now();
    
    console.log("AddPixelDialog handleOpenChange:", {
      newOpen,
      internalOpen,
      shouldBlockClose,
      closeInProgress: closeInProgressRef.current,
      dialogClosed: dialogClosedRef.current,
      timeSinceLastChange: now - lastOpenChangeTimestampRef.current
    });
    
    if (!newOpen && shouldBlockClose) {
      console.log("Blocking dialog close due to critical state");
      return;
    }
    
    if (!newOpen) {
      closeInProgressRef.current = true;
      
      // When manually closing, ensure we clean up the state properly
      if (pixelId) {
        console.log("Resetting pixel state before closing dialog");
        resetPixelId();
      }
      
      // Schedule closure with a small delay to allow state cleanup
      setTimeout(() => {
        setInternalOpen(false);
        onOpenChange(false);
        closeInProgressRef.current = false;
        
        // Mark dialog as fully closed for reopening
        dialogClosedRef.current = true;
        
        // Do full cleanup after dialog is closed
        completeCleanup();
      }, 50);
    } else {
      // Opening is always allowed
      setInternalOpen(true);
      onOpenChange(true);
      lastOpenChangeTimestampRef.current = now;
      
      // Reset the closed flag when opening
      dialogClosedRef.current = false;
    }
  }, [
    internalOpen, 
    isSubmitting,
    onOpenChange,
    pixelId,
    resetPixelId,
    completeCleanup
  ]);

  const handleAllowClose = useCallback(() => {
    console.log("AddPixelDialog: allowClose called, initiating complete shutdown");
    
    closeInProgressRef.current = true;
    
    if (pixelId) {
      resetPixelId();
    }
    
    // Use a small delay before actually closing the dialog
    setTimeout(() => {
      setInternalOpen(false);
      onOpenChange(false);
      closeInProgressRef.current = false;
      
      // Mark dialog as fully closed for reopening
      dialogClosedRef.current = true;
      
      // Do full cleanup
      completeCleanup();
    }, 100);
  }, [onOpenChange, pixelId, resetPixelId, completeCleanup]);

  return (
    <PixelDialogContent
      open={internalOpen}
      onOpenChange={handleOpenChange}
      pixelId={pixelId}
      isSubmitting={isSubmitting}
      onSubmit={createPixel}
      onTestPixel={testPixel}
      onAllowClose={handleAllowClose}
    />
  );
}
